import { ChiroUpAPI } from '@chiroup/client-core/functions/ChiroUpAPI';
import { SelectOption } from '@chiroup/components/form/Select';
import {
  itemToDataDescription,
  itemToOptionText,
} from '@chiroup/core/constants/DatabaseFeatureCommon';
import { useId, useMemo, useState } from 'react';
import { InfiniteData, QueryFunctionContext, useInfiniteQuery } from 'react-query';
import { kioskSession } from '../services/kioskSession';
import { requestSession } from '../services/requestSession';

// Ported from healthcom-app. The `/payors` endpoint lives on the
// schedule backend and derives `clinicId` from the active session
// (request | patient | kiosk), so we route through `api` (authed
// schedule API). The `clinicId` arg is still threaded through here for
// React Query cache scoping; it isn't sent on the wire.

// `main.tsx`'s global chain prefers a stale portal token over the live
// kiosk session, which 401s. `{}` rather than `{ Authorization: '' }`
// so the portal flow's global header survives — see `request.service.ts`.
const payorAuthHeaders = (kiosk?: boolean): Record<string, string> => {
  const token = kiosk ? kioskSession.getToken() : requestSession.getToken();

  if (!token) return {};

  return { Authorization: `Bearer ${token}` };
};

const query = ({ limit = 10, kiosk }: { limit: number; kiosk?: boolean }) => {
  return async (context: QueryFunctionContext) => {
    const search = context.queryKey[2];

    return ChiroUpAPI.get('api', `/payors`, {
      queryParams: {
        limit,
        search,
        page: context.pageParam,
      },
      headers: payorAuthHeaders(kiosk),
    });
  };
};

type InstanceReferenceType = {
  [key: string]: any;
  ID: number;
};

const useDatabaseAutocomplete = (clinicId: number, limit = 10, kiosk?: boolean) => {
  const [search, setSearch] = useState('');

  const { data, isFetching, isError, fetchNextPage, hasNextPage } = useInfiniteQuery<{
    data: InstanceReferenceType[];
    count: number;
    page: number;
  }>([useId(), clinicId, search], query({ limit, kiosk }), {
    getNextPageParam: lastGroup => {
      if (!lastGroup) return;

      return lastGroup.data?.length === 10 ? (lastGroup.page || 0) + 1 : undefined;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    placeholderData: {
      count: 0,
      page: 0,
      data: [] as InstanceReferenceType[],
      pageParams: [],
      pages: [],
    } as InfiniteData<any>,
  });

  const options: SelectOption[] = useMemo(() => {
    if (!data) return [];

    return data.pages
      .reduce((acc: any, curr) => {
        return [...acc, ...curr.data];
      }, [] as any[])
      .map((row: any) => ({
        text: itemToOptionText('payors', row),
        value: row.ID,
        data: { description: itemToDataDescription('payors', row) },
      }));
  }, [data]);

  const onChangeSearch = (value: string) => setSearch(value);

  return { options, isFetching, isError, onChangeSearch, fetchNextPage, hasNextPage, search };
};

export default useDatabaseAutocomplete;
